// src/scripts/utilities/image-scrubber.js
gsap.registerPlugin(ScrollTrigger);
var ImageScrubber = class {
  constructor({
    canvasId,
    trigger,
    imageName,
    startFrame,
    endFrame,
    targetHeight,
    targetWidth,
    gsapParams,
    gsapSequence,
    scrollTriggerParams,
    onScrubCompleteForward = () => {
    },
    onScrubCompleteReverse = () => {
    },
    // Optional infinite yoyo settings:
    // { startFrame: "XX", endFrame: "YY", duration: 0.7 }
    infiniteYoyo = null,
    origin = "center",
    fillViewport = false,
    onSimulateCompleteForward = () => {
    },
    scaleFactor: scaleFactor2 = 1
  } = {}) {
    this.canvasId = canvasId;
    this.trigger = trigger;
    this.imageName = imageName;
    this.startFrame = startFrame;
    this.endFrame = endFrame;
    this.targetHeight = targetHeight;
    this.targetWidth = targetWidth;
    this.gsapParams = gsapParams;
    this.gsapSequence = gsapSequence;
    this.scrollTriggerParams = scrollTriggerParams;
    this.onScrubCompleteForward = onScrubCompleteForward;
    this.onScrubCompleteReverse = onScrubCompleteReverse;
    this.infiniteYoyo = infiniteYoyo;
    this.onSimulateCompleteForward = onSimulateCompleteForward;
    this.canvas = document.getElementById(this.canvasId);
    this.context = this.canvas.getContext("2d");
    this.origin = origin;
    this.fillViewport = fillViewport;
    this.scaleFactor = scaleFactor2;
    this.loadedImages = 0;
    this.imageUrls = document.querySelector(`input[data-image-array='${this.imageName}']`)?.value.split(",");
    this.images = [];
    this.imageUrls?.forEach((src) => {
      const img = new Image();
      img.onload = () => {
        this.loadedImages++;
        if (this.loadedImages === this.imageUrls.length - 1) {
          document.dispatchEvent(
            new CustomEvent("imageArrayLoaded", {
              detail: { imageArrayName: this.imageName }
            })
          );
        }
      };
      const url = `${window.location.protocol}${src}`;
      img.src = url;
      this.images.push(img);
    });
    this.scrubArray = { frame: 0 };
    this.render = this.render.bind(this);
    this.resizeCanvas = this.resizeCanvas.bind(this);
    this.startInfiniteYoyo = this.startInfiniteYoyo.bind(this);
    this.killInfiniteYoyo = this.killInfiniteYoyo.bind(this);
    window.addEventListener("resize", this.resizeCanvas);
    this.resizeCanvas();
    this.infiniteTimeline = null;
    this.infiniteActive = false;
  }
  destroy() {
    window.removeEventListener("resize", this.resizeCanvas);
    if (this.scrubTween) {
      this.scrubTween.kill();
      this.scrubTween = null;
    }
    if (this.infiniteTimeline) {
      this.infiniteTimeline.kill();
      this.infiniteTimeline = null;
      this.infiniteActive = false;
    }
    this.context.clearRect(0, 0, this.canvas.width, this.canvas.height);
  }
  init() {
    if (this.images.length > 0) {
      this.images[0].onload = this.render;
    } else {
      console.warn("No images loaded", this.imageName);
    }
  }
  // Starts the infinite yoyo tween over the defined sub-range.
  startInfiniteYoyo() {
    if (!this.infiniteYoyo) return;
    if (this.infiniteActive) return;
    this.infiniteActive = true;
    this.infiniteScrub = { frame: 0 };
    const frameRange = parseInt(this.infiniteYoyo.endFrame, 10) - parseInt(this.infiniteYoyo.startFrame, 10);
    this.infiniteTimeline = gsap.timeline({ repeat: -1, yoyo: true });
    this.infiniteTimeline.to(this.infiniteScrub, {
      frame: frameRange,
      duration: this.infiniteYoyo.duration || 0.7,
      ease: "none",
      onUpdate: this.render
    });
  }
  // Kills the infinite yoyo tween.
  killInfiniteYoyo() {
    if (this.infiniteTimeline) {
      this.infiniteTimeline.kill();
      this.infiniteTimeline = null;
    }
    this.infiniteActive = false;
    if (this.infiniteScrub) {
      this.infiniteScrub.frame = 0;
    }
    this.render();
  }
  // Returns the main scrub tween. If options.infiniteAfterScrub is true,
  // the onComplete callback will trigger infinite yoyo mode.
  // If reverse, we add an onStart callback that kills any active infinite yoyo.
  getScrubTween(options = {}) {
    const timelineMode = options.timelineMode === true;
    const baseTweenParams = {
      snap: "frame",
      ease: "none",
      immediateRender: timelineMode ? false : true,
      ...this.gsapParams,
      onUpdate: () => {
        const progress = this.scrubArray.frame / Math.max(this.images.length - 100, 1);
        if (progress > 0.95 && !this.simulateComplete) {
          this.onSimulateCompleteForward();
          this.simulateComplete = true;
        } else if (progress < 0.8) {
          this.simulateComplete = false;
        }
        this.render();
      }
    };
    if (!timelineMode && this.scrollTriggerParams) {
      baseTweenParams.scrollTrigger = {
        scrub: 0.5,
        trigger: this.trigger,
        pin: true,
        pinSpacing: true,
        ...this.scrollTriggerParams
      };
    }
    if (!options.reverse) {
      if (options.infiniteAfterScrub && this.infiniteYoyo) {
        baseTweenParams.onComplete = () => {
          this.onScrubCompleteForward();
          setTimeout(() => {
            if (this.scrubArray.frame === this.images.length - 1) {
              this.startInfiniteYoyo();
            }
          }, 100);
        };
      } else {
        baseTweenParams.onComplete = this.onScrubCompleteForward;
      }
      return gsap.fromTo(
        this.scrubArray,
        { frame: 0 },
        { frame: this.images.length - 1, ...baseTweenParams },
        this.gsapSequence
      );
    } else {
      baseTweenParams.onStart = () => {
        if (this.infiniteActive) {
          this.killInfiniteYoyo();
        }
      };
      baseTweenParams.onComplete = this.onScrubCompleteReverse;
      return gsap.fromTo(
        this.scrubArray,
        { frame: this.images.length - 1 },
        { frame: 0, ...baseTweenParams },
        this.gsapSequence
      );
    }
  }
  render() {
    this.context.clearRect(0, 0, this.canvas.width, this.canvas.height);
    let img;
    if (this.infiniteActive && this.infiniteYoyo) {
      const offset = parseInt(this.infiniteYoyo.startFrame, 10) - parseInt(this.startFrame, 10);
      const index = Math.min(offset + Math.floor(this.infiniteScrub.frame), this.images.length - 1);
      img = this.images[index];
    } else {
      img = this.images[this.scrubArray.frame];
    }
    if (img && img.complete && (img.naturalWidth || 0) > 0) {
      const naturalWidth = img.naturalWidth || img.width;
      const naturalHeight = img.naturalHeight || img.height;
      let computedWidth, computedHeight;
      if (this.fillViewport) {
        const scaleX = this.canvas.width / naturalWidth;
        const scaleY = this.canvas.height / naturalHeight;
        const scale = Math.max(scaleX, scaleY);
        computedWidth = naturalWidth * scale * this.scaleFactor;
        computedHeight = naturalHeight * scale * this.scaleFactor;
      } else if (this.targetWidth || this.targetHeight) {
        if (this.targetWidth && this.targetHeight) {
          const scale = Math.min(this.targetWidth / naturalWidth, this.targetHeight / naturalHeight);
          computedWidth = naturalWidth * scale * this.scaleFactor;
          computedHeight = naturalHeight * scale * this.scaleFactor;
        } else if (this.targetWidth) {
          const scale = this.targetWidth / naturalWidth;
          computedWidth = this.targetWidth * this.scaleFactor;
          computedHeight = naturalHeight * scale * this.scaleFactor;
        } else {
          const scale = this.targetHeight / naturalHeight;
          computedHeight = this.targetHeight * this.scaleFactor;
          computedWidth = naturalWidth * scale * this.scaleFactor;
        }
      } else {
        const scale = this.canvas.width / naturalWidth;
        computedWidth = this.canvas.width * this.scaleFactor;
        computedHeight = naturalHeight * scale * this.scaleFactor;
      }
      let xOffset;
      if (this.origin === "left") {
        xOffset = 0;
      } else if (this.origin === "right") {
        xOffset = this.canvas.width - computedWidth;
      } else {
        xOffset = (this.canvas.width - computedWidth) / 2;
      }
      const yOffset = (this.canvas.height - computedHeight) / 2;
      this.context.drawImage(img, xOffset, yOffset, computedWidth, computedHeight);
    }
  }
  resizeCanvas() {
    this.canvas.width = window.innerWidth;
    this.canvas.height = window.innerHeight;
    this.render();
  }
};

// src/scripts/cant-get-enough-lp.js
gsap.registerPlugin(ScrollTrigger);
var isMobile = window.matchMedia("(max-width: 768px)");
var windowWidth = window.innerWidth;
var windowHeight = window.innerHeight;
window.addEventListener("resize", () => {
  windowWidth = window.innerWidth;
  windowHeight = window.innerHeight;
});
function calculateScaleFactor(minScale, maxScale) {
  if (!isMobile.matches) return 1;
  const minWidth = 320;
  const maxWidth = 768;
  const clampedWidth = Math.max(minWidth, Math.min(maxWidth, windowWidth));
  const progress = (clampedWidth - minWidth) / (maxWidth - minWidth);
  return minScale - progress * (minScale - maxScale);
}
var scaleFactor = calculateScaleFactor(3.2, 1);
var BottleAndFragrance = class {
  constructor() {
    this.currentProductIndex = 0;
    this.init();
  }
  init() {
    document.addEventListener("imageArrayLoaded", (e) => {
      if (e.detail.imageArrayName !== "cge-bottle-imgs") return;
      this.setupAnimation();
      this.hideLoadingIndicator();
    });
    this.setupScrubber();
  }
  setupScrubber() {
    this.bottleScrubber = new ImageScrubber({
      canvasId: "bottle-canvas",
      trigger: "#bottle-animation-section",
      imageName: "cge-bottle-imgs",
      scaleFactor
    });
    this.bottleScrubber.init();
    this.fragranceScrubber = new ImageScrubber({
      canvasId: "fragrance-canvas",
      trigger: "#fragrance-section",
      imageName: "cge-fragrance-imgs",
      fillViewport: true
    });
    this.fragranceScrubber.init();
  }
  updateFragranceDetail(targetIndex) {
    const fragranceDetails = Array.from(document.querySelectorAll("[data-fragrance-details]"));
    const fragranceNames = Array.from(document.querySelectorAll("[data-fragrance-name]"));
    if (targetIndex < 0 || targetIndex >= fragranceDetails.length) {
      return;
    }
    fragranceDetails.forEach((detail, index) => {
      if (index !== targetIndex) {
        gsap.to(detail, {
          translateY: "-150%",
          opacity: 0,
          duration: 0.4,
          ease: "power2.inOut",
          onComplete: () => {
            gsap.set(detail, { display: "none" });
          }
        });
      }
    });
    fragranceNames.forEach((name, index) => {
      if (index !== targetIndex) {
        gsap.to(name, {
          translateY: "-100%",
          duration: 0.4,
          ease: "power2.inOut",
          onComplete: () => {
            gsap.set(name, { display: "none" });
          }
        });
      }
    });
    const targetDetail = fragranceDetails[targetIndex];
    const targetName = fragranceNames[targetIndex];
    gsap.delayedCall(0.6, () => {
      gsap.set(targetDetail, { display: "block", translateY: "50%" });
      gsap.set(targetName, { display: "block", translateY: "100%" });
      gsap.delayedCall(0.2, () => {
        gsap.to(targetDetail, {
          translateY: "-50%",
          opacity: 1,
          duration: 0.5,
          ease: "power2.out"
        });
        gsap.to(targetName, {
          translateY: "0%",
          duration: 0.5,
          ease: "power2.out"
        });
      });
    });
  }
  setupAnimation() {
    const canvas = document.getElementById("bottle-canvas");
    const coverVideo = document.querySelector(".cover-video");
    const bgVideo = document.querySelector(".background-video");
    const bottleTextHeading = document.querySelector(".bottle-heading");
    const bottleTextSubHeading = document.querySelector(".bottle-subheading");
    const announcementBarWrapper = document.querySelector(".announcement-bar-wrapper");
    const announcementBar = document.querySelector(".bottle-announcement-bar");
    const fragranceSection = document.querySelector(".fragrance-section");
    const bottleTextProductContainerHeader = document.querySelector(".bottle-text-product-container-header-text");
    const bottleCartContainer = document.querySelector(".bottle-cart-container");
    const fragranceAddToCart = document.querySelector(".fragrance-add-to-cart-wrapper");
    const fragranceAddToCartWrapper = document.querySelector(".fragrance-add-to-cart-wrapper .fragrance-add-to-cart");
    const multiplier = calculateScaleFactor(4, 1);
    gsap.set(canvas, { scale: 6.5 * multiplier / scaleFactor, transformOrigin: "center center" });
    gsap.set(coverVideo, {
      scale: multiplier,
      opacity: 1,
      marginLeft: 0,
      clipPath: "inset(0% 0% 0% 0%)",
      transformOrigin: "center center"
    });
    gsap.set(bgVideo, { scale: 1, opacity: 1, transformOrigin: "center center" });
    gsap.set(bottleTextHeading, { x: "-100vw" });
    gsap.set(bottleTextSubHeading, { x: "100vw" });
    gsap.set(bottleTextProductContainerHeader, { y: "75%", opacity: 0 });
    gsap.set(bottleCartContainer, { y: "75%", opacity: 0 });
    gsap.set(fragranceAddToCartWrapper, { opacity: 1 });
    gsap.set(announcementBarWrapper, { opacity: 1 });
    gsap.set(announcementBar, { opacity: 1 });
    this.masterTimeline = gsap.timeline({
      scrollTrigger: {
        trigger: "#bottle-page-wrapper",
        start: "top top",
        end: "bottom bottom",
        scrub: 1,
        pin: true,
        anticipatePin: 1,
        pinSpacing: false
      }
    });
    this.masterTimeline.to(
      announcementBarWrapper,
      {
        opacity: 0,
        duration: 0.5,
        ease: "power1.out"
      },
      0
    );
    this.masterTimeline.to(announcementBar, { opacity: 0, duration: 0.5, ease: "power1.out" }, "<");
    this.masterTimeline.to(canvas, { scale: 1, duration: 4 });
    const viewportAspectRatio = windowWidth / windowHeight;
    const targetAspectRatio = 181 / 93;
    let clipPath;
    if (viewportAspectRatio > targetAspectRatio) {
      const cropPercent = (viewportAspectRatio - targetAspectRatio) / viewportAspectRatio * 50;
      clipPath = `inset(0% ${cropPercent}% 0% ${cropPercent}%)`;
    } else {
      const cropPercent = (targetAspectRatio - viewportAspectRatio) / targetAspectRatio * 50;
      clipPath = `inset(${cropPercent}% 0% ${cropPercent}% 0%)`;
    }
    this.masterTimeline.to(coverVideo, { marginLeft: "0.5vmax", clipPath, duration: 0.2 }, "<");
    this.masterTimeline.to(coverVideo, { scale: 0.15 * scaleFactor, duration: 4 }, "<");
    this.masterTimeline.to(coverVideo, { opacity: 0, duration: 0.4 }, "-=0.4");
    this.bottleScrubberControl = { frame: 0 };
    this.masterTimeline.to(
      this.bottleScrubberControl,
      {
        frame: 68,
        duration: 2,
        ease: "none",
        onUpdate: () => {
          this.bottleScrubber.scrubArray.frame = Math.floor(this.bottleScrubberControl.frame);
          this.bottleScrubber.render();
        }
      },
      ">+2"
    );
    this.masterTimeline.to(bottleTextHeading, { x: "100vw", duration: 16, ease: "power2.out" }, "<2.6");
    this.masterTimeline.to(bottleTextSubHeading, { x: "-100vw", duration: 16, ease: "power2.out" }, "<");
    this.masterTimeline.to(
      bottleTextProductContainerHeader,
      { y: "0%", opacity: 1, duration: 2, ease: "power2.out" },
      ">"
    );
    this.masterTimeline.to(bottleCartContainer, { y: "0%", opacity: 1, duration: 2, ease: "power2.out" }, "<");
    this.masterTimeline.to(fragranceAddToCartWrapper, { opacity: 0, duration: 2, ease: "power2.out" }, "<-1");
    this.masterTimeline.to(
      fragranceAddToCart,
      { translateY: isMobile.matches ? "99px" : "48px", duration: 2, ease: "power2.out" },
      "<"
    );
    this.masterTimeline.to(bottleTextProductContainerHeader, { opacity: 0, duration: 1, ease: "power2.out" }, ">+6");
    this.masterTimeline.to(bottleCartContainer, { opacity: 0, duration: 1, ease: "power2.out" }, "<");
    this.masterTimeline.to(fragranceAddToCartWrapper, { opacity: 1, duration: 1, ease: "power2.out" }, "<+0.5");
    this.masterTimeline.to(fragranceAddToCart, { translateY: 0, duration: 2, ease: "power2.out" }, "<");
    this.masterTimeline.to(bgVideo, { opacity: 0, duration: 1, ease: "power2.out" }, "<+1");
    this.masterTimeline.to(
      this.bottleScrubberControl,
      {
        frame: 130,
        duration: 2,
        ease: "none",
        onUpdate: () => {
          this.bottleScrubber.scrubArray.frame = Math.floor(this.bottleScrubberControl.frame);
          this.bottleScrubber.render();
        }
      },
      "<"
    );
    this.masterTimeline.to(fragranceSection, { opacity: 1, duration: 2 }, ">+2");
    const fragranceDetails = Array.from(document.querySelectorAll("[data-fragrance-details]"));
    const fragranceNames = Array.from(document.querySelectorAll("[data-fragrance-name]"));
    gsap.set(fragranceDetails, { translateY: "50%", opacity: 0, display: "none" });
    gsap.set(fragranceNames, { translateY: "100%", opacity: 1, display: "none" });
    if (fragranceDetails.length > 0) {
      gsap.set(fragranceDetails[0], { display: "block", translateY: "-50%", opacity: 1 });
    }
    if (fragranceNames.length > 0) {
      gsap.set(fragranceNames[0], { display: "block", translateY: "0%" });
    }
    this.currentFragranceIndex = 0;
    ScrollTrigger.create({
      trigger: fragranceSection,
      start: "top top",
      end: "bottom bottom",
      scrub: 1,
      pin: true,
      pinSpacing: false,
      onUpdate: (self) => {
        const progress = self.progress;
        this.fragranceScrubber.scrubArray.frame = Math.min(
          Math.floor(progress * this.fragranceScrubber.images.length),
          this.fragranceScrubber.images.length - 1
        );
        this.fragranceScrubber.render();
        let textOpacity = 1;
        if (progress > 0.9) {
          textOpacity = (1 - progress) / 0.1;
        }
        fragranceDetails.forEach((detail) => {
          detail.style.opacity = textOpacity;
        });
        fragranceNames.forEach((name) => {
          name.style.opacity = textOpacity;
        });
        let targetIndex = -1;
        if (progress < 0.25) {
          targetIndex = 0;
        } else if (progress < 0.5) {
          targetIndex = 1;
        } else if (progress < 0.75) {
          targetIndex = 2;
        } else {
          targetIndex = 3;
        }
        if (targetIndex !== this.currentFragranceIndex) {
          this.updateFragranceDetail(targetIndex);
          this.currentFragranceIndex = targetIndex;
        }
      }
    });
  }
  hideLoadingIndicator() {
    const loadingEl = document.getElementById("bottle-loading");
    if (loadingEl) {
      gsap.to(loadingEl, { opacity: 0, duration: 0.5, onComplete: () => loadingEl.style.display = "none" });
    }
  }
  destroy() {
    if (this.bottleScrubber) this.bottleScrubber.destroy();
    if (this.fragranceScrubber) this.fragranceScrubber.destroy();
    if (this.masterTimeline) this.masterTimeline.kill();
    ScrollTrigger.getAll().forEach((t) => t.kill());
  }
};
new BottleAndFragrance();
